import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import type {
  VirtualEnvironment,
  VirtualHost,
  VirtualHostDetail,
  VirtualNode,
} from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Ambienti virtuali come **panoramica di cosa c'è**, dall'alto verso il basso:
 *
 * 1. la striscia dei totali su *tutti* gli ambienti, con i numeri che dicono
 *    subito se il parco è sovraccarico (overcommit, RAM, storage);
 * 2. una **card per ambiente** — un piccolo cruscotto ciascuna, con i tre
 *    indicatori che contano — da cui si sceglie;
 * 3. cluster → host dell'ambiente scelto, e la scheda dell'host con due viste:
 *    tabella (VM, datastore, reti) e mappa delle correlazioni a settori.
 *
 * I numeri sono somme dell'inventario, non le proiezioni del capacity plan:
 * rispondono a «quanto ho e quanto ne uso», non a «quando finisce».
 */
export default function VirtualMap() {
  const ambienti = useAsync(() => api.virtualMap(), [])

  const [sorgente, setSorgente] = useState<string | null>(null)
  const [hostId, setHostId] = useState<string | null>(null)

  const dati = ambienti.data ?? []
  const ambiente = useMemo(
    () => dati.find((a) => a.source === sorgente) ?? dati[0],
    [dati, sorgente],
  )

  if (ambienti.loading && !ambienti.data) return <Spinner />

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Ambienti virtuali')}</h1>
          <p className="page__subtitle">
            {t('Visualizza cluster, host e macchine virtuali rilevati dai connettori di virtualizzazione ed evidenzia le relazioni e la distribuzione all’interno degli ambienti gestiti.')}
          </p>
        </div>
      </header>

      <ErrorBanner error={ambienti.error} />

      {dati.length === 0 ? (
        <EmptyState
          title={t('Nessun ambiente virtuale')}
          hint={t('Compaiono qui dopo una discovery con un connettore di virtualizzazione (VMware, Nutanix, Proxmox…).')}
        />
      ) : (
        <>
          <Cruscotto ambienti={dati} />
          <div className="venv-cards">
            {dati.map((a) => (
              <CardAmbiente
                key={a.source}
                ambiente={a}
                scelto={a.source === (ambiente?.source ?? null)}
                onScegli={() => {
                  setSorgente(a.source)
                  setHostId(null)
                }}
              />
            ))}
          </div>
          <SezioneVersioni ambiente={ambiente} />
          <div className="vmap vmap--due">
            <ColonnaClusterHost
              ambiente={ambiente}
              hostScelto={hostId}
              onScegliHost={setHostId}
            />
            <SchedaHost hostId={hostId} />
          </div>
        </>
      )}
    </>
  )
}

// ------------------------------------------------- cruscotto (totali globali)

type Totali = {
  ambienti: number
  clusters: number
  hosts: number
  vms: number
  vms_running: number
  vcpu: number
  cores: number
  mem_alloc: number
  mem_phys: number
  storage_used: number
  storage_cap: number
}

function somma(ambienti: VirtualEnvironment[]): Totali {
  const t0: Totali = {
    ambienti: ambienti.length, clusters: 0, hosts: 0, vms: 0, vms_running: 0,
    vcpu: 0, cores: 0, mem_alloc: 0, mem_phys: 0, storage_used: 0, storage_cap: 0,
  }
  return ambienti.reduce((acc, a) => {
    const s = a.summary
    acc.clusters += s.clusters
    acc.hosts += s.hosts
    acc.vms += s.vms
    acc.vms_running += s.vms_running
    acc.vcpu += s.vcpu_allocated
    acc.cores += s.physical_cores
    acc.mem_alloc += s.memory_allocated_bytes
    acc.mem_phys += s.memory_physical_bytes
    acc.storage_used += s.storage_used_bytes
    acc.storage_cap += s.storage_capacity_bytes
    return acc
  }, t0)
}

/** Il colore semantico di un overcommit CPU: informa senza allarmare. */
function tonoOvercommit(r: number | null): 'ok' | 'warn' | 'err' | '' {
  if (r == null) return ''
  if (r <= 1.5) return 'ok'
  if (r <= 4) return ''
  if (r <= 6) return 'warn'
  return 'err'
}

function tonoPercento(p: number | null): 'ok' | 'warn' | 'err' | '' {
  if (p == null) return ''
  if (p < 70) return 'ok'
  if (p < 85) return ''
  if (p < 95) return 'warn'
  return 'err'
}

function Cruscotto({ ambienti }: { ambienti: VirtualEnvironment[] }) {
  const tot = somma(ambienti)
  const ocCpu = tot.cores ? tot.vcpu / tot.cores : null
  const pRam = tot.mem_phys ? (tot.mem_alloc / tot.mem_phys) * 100 : null
  const pStorage = tot.storage_cap ? (tot.storage_used / tot.storage_cap) * 100 : null
  const pVm = tot.vms ? (tot.vms_running / tot.vms) * 100 : null

  return (
    <div className="venv-kpi">
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{tot.ambienti}</span>
        <span className="venv-kpi__label">{t('Ambienti')}</span>
        <span className="venv-kpi__sub">{t('{n} cluster', { n: tot.clusters })} · {t('{n} host', { n: tot.hosts })}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{tot.vms_running}<span className="venv-kpi__den">/{tot.vms}</span></span>
        <span className="venv-kpi__label">{t('VM accese')}</span>
        {pVm != null && <MiniBarra percent={pVm} tono="ok" />}
      </div>
      <div className="venv-kpi__tile">
        <span className={`venv-kpi__value venv-kpi__value--${tonoOvercommit(ocCpu)}`}>
          {ocCpu != null ? `${ocCpu.toFixed(ocCpu >= 10 ? 0 : 1)}×` : '—'}
        </span>
        <span className="venv-kpi__label">{t('Overcommit CPU')}</span>
        <span className="venv-kpi__sub">{t('{vcpu} vCPU · {n} core', { vcpu: tot.vcpu, n: tot.cores })}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className={`venv-kpi__value venv-kpi__value--${tonoPercento(pRam)}`}>
          {pRam != null ? `${Math.round(pRam)}%` : '—'}
        </span>
        <span className="venv-kpi__label">{t('RAM allocata')}</span>
        <span className="venv-kpi__sub">{t('{a} su {v} fisici', { a: fmtBytes(tot.mem_alloc), v: fmtBytes(tot.mem_phys) })}</span>
        {pRam != null && <MiniBarra percent={pRam} tono={tonoPercento(pRam)} />}
      </div>
      <div className="venv-kpi__tile">
        <span className={`venv-kpi__value venv-kpi__value--${tonoPercento(pStorage)}`}>
          {pStorage != null ? `${Math.round(pStorage)}%` : '—'}
        </span>
        <span className="venv-kpi__label">{t('Storage usato')}</span>
        <span className="venv-kpi__sub">{fmtBytes(tot.storage_used)} / {fmtBytes(tot.storage_cap)}</span>
        {pStorage != null && <MiniBarra percent={pStorage} tono={tonoPercento(pStorage)} />}
      </div>
    </div>
  )
}

/** Una barra sottile con il tono semantico: il «quanto» in un colpo d'occhio. */
function MiniBarra({ percent, tono }: { percent: number; tono: 'ok' | 'warn' | 'err' | '' }) {
  const p = Math.max(0, Math.min(100, percent))
  return (
    <span className="venv-mini">
      <span className={`venv-mini__fill venv-mini__fill--${tono || 'acc'}`} style={{ width: `${p}%` }} />
    </span>
  )
}

// ------------------------------------------------- le card degli ambienti

function CardAmbiente({
  ambiente,
  scelto,
  onScegli,
}: {
  ambiente: VirtualEnvironment
  scelto: boolean
  onScegli: () => void
}) {
  const s = ambiente.summary
  const ocCpu = s.physical_cores ? s.vcpu_allocated / s.physical_cores : null
  const pRam = s.memory_physical_bytes
    ? (s.memory_allocated_bytes / s.memory_physical_bytes) * 100 : null
  const pStorage = s.storage_capacity_bytes
    ? (s.storage_used_bytes / s.storage_capacity_bytes) * 100 : null

  return (
    <button type="button" onClick={onScegli}
            className={`venv-card${scelto ? ' venv-card--on' : ''}`}>
      <span className="venv-card__testata">
        <span className="venv-card__nome">{ambiente.label}</span>
        <span className="venv-card__fonte mono">{ambiente.source}</span>
      </span>
      <span className="venv-card__conta">
        {t('{n} host', { n: s.hosts })}
        {s.clusters > 0 && <> · {t('{n} cluster', { n: s.clusters })}</>}
        {' · '}{s.vms_running}/{s.vms} VM
      </span>
      <span className="venv-card__metri">
        <MetroCard label="CPU"
                   testo={ocCpu != null ? `${ocCpu.toFixed(1)}×` : '—'}
                   percent={ocCpu != null ? Math.min(100, ocCpu * 25) : null}
                   tono={tonoOvercommit(ocCpu)} />
        <MetroCard label="RAM"
                   testo={pRam != null ? `${Math.round(pRam)}%` : '—'}
                   percent={pRam} tono={tonoPercento(pRam)} />
        <MetroCard label={t('Storage')}
                   testo={pStorage != null ? `${Math.round(pStorage)}%` : '—'}
                   percent={pStorage} tono={tonoPercento(pStorage)} />
      </span>
    </button>
  )
}

function MetroCard({
  label, testo, percent, tono,
}: { label: string; testo: string; percent: number | null; tono: 'ok' | 'warn' | 'err' | '' }) {
  return (
    <span className="venv-metro">
      <span className="venv-metro__testa">
        <span className="venv-metro__label">{label}</span>
        <span className={`venv-metro__valore venv-metro__valore--${tono}`}>{testo}</span>
      </span>
      {percent != null ? (
        <MiniBarra percent={percent} tono={tono} />
      ) : (
        <span className="venv-mini" />
      )}
    </span>
  )
}

// -------------------------------------- colonna: cluster e host dell'ambiente

function ColonnaClusterHost({
  ambiente,
  hostScelto,
  onScegliHost,
}: {
  ambiente: VirtualEnvironment | undefined
  hostScelto: string | null
  onScegliHost: (id: string) => void
}) {
  const [aperti, setAperti] = useState<Record<string, boolean>>({})
  const [filtro, setFiltro] = useState('')
  useEffect(() => {
    setAperti({})
    setFiltro('')
  }, [ambiente?.source])

  if (!ambiente) return <Card title={t('Cluster e host')}> </Card>

  // Con pochi host i cluster stanno aperti; con centinaia (un vCenter da 256
  // host e 4000 VM) la pagina diventava un elenco chilometrico: si parte
  // chiusi e si apre quello che serve, o si filtra per nome e indirizzo.
  const totaleHost = ambiente.summary.hosts
  const tanti = totaleHost > 40
  const q = filtro.trim().toLowerCase()
  const combacia = (h: VirtualHost) =>
    !q || h.name.toLowerCase().includes(q) || (h.mgmt_ip ?? '').toLowerCase().includes(q)
  const apertoDefault = (id: string) => (q ? true : (aperti[id] ?? !tanti))
  const maxVm = Math.max(
    1,
    ...ambiente.clusters.flatMap((c) => c.hosts.map((h) => h.vm_count)),
    ...ambiente.standalone_hosts.map((h) => h.vm_count),
  )
  const clusterVisibili = ambiente.clusters
    .map((c) => ({ ...c, hosts: c.hosts.filter(combacia) }))
    .filter((c) => c.hosts.length > 0)
  const standaloneVisibili = ambiente.standalone_hosts.filter(combacia)

  return (
    <Card title={t('{env} · cluster e host', { env: ambiente.label })}>
      {ambiente.clusters.length === 0 && ambiente.standalone_hosts.length === 0 ? (
        <p className="muted">{t('Nessun host in questo ambiente.')}</p>
      ) : (
        <>
        {tanti && (
          <div className="vmap__filtro">
            <input
              type="search"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder={t('filtra host per nome o indirizzo…')}
              aria-label={t('Filtra gli host')}
            />
            <span className="muted">
              {q
                ? t('{n} host su {tot}', { n: clusterVisibili.reduce((k, c) => k + c.hosts.length, 0) + standaloneVisibili.length, tot: totaleHost })
                : t('{n} host in {c} cluster: apri un cluster o filtra', { n: totaleHost, c: ambiente.clusters.length })}
            </span>
          </div>
        )}
        <ul className="vmap__list">
          {clusterVisibili.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="vmap__cluster"
                onClick={() => setAperti((a) => ({ ...a, [c.id]: !apertoDefault(c.id) }))}
              >
                <span className="vmap__caret">{apertoDefault(c.id) ? '▾' : '▸'}</span>
                <span className="vmap__name">{c.name}</span>
                <span className="vmap__count">
                  {t('{n} host', { n: c.host_count })}
                  {' · '}
                  {t('{n} VM', { n: c.hosts.reduce((k, h) => k + h.vm_count, 0) })}
                </span>
              </button>
              {apertoDefault(c.id) && (
                <ul className="vmap__sublist">
                  {c.hosts.map((h) => (
                    <RigaHost key={h.id} host={h} maxVm={maxVm}
                              scelto={h.id === hostScelto}
                              onScegli={() => onScegliHost(h.id)} />
                  ))}
                </ul>
              )}
            </li>
          ))}
          {standaloneVisibili.length > 0 && (
            <li>
              <div className="vmap__gruppo">{t('Senza cluster')}</div>
              <ul className="vmap__sublist">
                {standaloneVisibili.map((h) => (
                  <RigaHost key={h.id} host={h} maxVm={maxVm}
                            scelto={h.id === hostScelto}
                            onScegli={() => onScegliHost(h.id)} />
                ))}
              </ul>
            </li>
          )}
        </ul>
        </>
      )}
    </Card>
  )
}

// ------------------------------------- versioni: vCenter e host dell'ambiente

function SezioneVersioni({ ambiente }: { ambiente: VirtualEnvironment | undefined }) {
  if (!ambiente) return null

  const m = ambiente.manager
  // Ogni host con l'etichetta del suo cluster, per dire «chi gira cosa».
  const righe: { id: string; name: string; cluster: string; software_rev: string }[] = []
  for (const c of ambiente.clusters) {
    for (const h of c.hosts) {
      righe.push({ id: h.id, name: h.name, cluster: c.name, software_rev: h.software_rev ?? '' })
    }
  }
  for (const h of ambiente.standalone_hosts) {
    righe.push({ id: h.id, name: h.name, cluster: '—', software_rev: h.software_rev ?? '' })
  }
  righe.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  const conCluster = ambiente.clusters.length > 0

  // Una riga per **versione**, non per host: con 256 host la tabella era una
  // pagina intera per dire tre numeri. «8.0.1 · 53 host», e si apre per vedere
  // quali. La versione più diffusa prima; «non rilevata» in fondo.
  const gruppi = new Map<string, typeof righe>()
  for (const r of righe) {
    const chiave = r.software_rev || ''
    gruppi.set(chiave, [...(gruppi.get(chiave) ?? []), r])
  }
  const versioni = [...gruppi.entries()]
    .sort((a, b) => (a[0] === '' ? 1 : b[0] === '' ? -1 : b[1].length - a[1].length))

  // Niente da mostrare: né gestore né host con versione. Non si occupa spazio a vuoto.
  if (!m && righe.every((r) => !r.software_rev)) return null

  const vcenter = m
    ? [
        m.full_name,
        m.full_name ? undefined : m.version && t('versione {v}', { v: m.version }),
      ].filter(Boolean).join('')
    : ''

  return (
    <Card title={t('{env} · versioni', { env: ambiente.label })}>
      {m && (
        <div className="venv-versioni__gestore">
          <span className="venv-versioni__nome">{vcenter || t('Gestore')}</span>
          <span className="venv-versioni__chips">
            {m.version && <span className="chip">{t('Versione {v}', { v: m.version })}</span>}
            {m.build && <span className="chip">{t('Build')} {m.build}</span>}
            {m.api_version && <span className="chip">{t('API')} {m.api_version}</span>}
          </span>
        </div>
      )}
      {versioni.length > 0 && (
        <ul className="venv-versioni__gruppi">
          {versioni.map(([versione, hosts]) => (
            <GruppoVersione key={versione || '—'} versione={versione} hosts={hosts} conCluster={conCluster}
                            apertoAllInizio={versioni.length === 1 && hosts.length <= 12} />
          ))}
        </ul>
      )}
    </Card>
  )
}

type HostVersione = { id: string; name: string; cluster: string }

function GruppoVersione({ versione, hosts, conCluster, apertoAllInizio }: {
  versione: string
  hosts: HostVersione[]
  conCluster: boolean
  apertoAllInizio: boolean
}) {
  const [aperto, setAperto] = useState(apertoAllInizio)
  // I cluster in cui sta questa versione: dice «dove» senza aprire.
  const cluster = conCluster ? [...new Set(hosts.map((h) => h.cluster))] : []
  return (
    <li className="venv-versioni__gruppo">
      <button type="button" className="venv-versioni__toggle" onClick={() => setAperto((a) => !a)}>
        <span className="vmap__caret">{aperto ? '▾' : '▸'}</span>
        <span className="venv-versioni__versione">
          {versione ? t('Versione {v}', { v: versione }) : t('Versione non rilevata')}
        </span>
        <span className="vmap__count">{t('{n} host', { n: hosts.length })}</span>
        {cluster.length > 0 && (
          <span className="muted venv-versioni__dove">
            {cluster.length <= 3 ? cluster.join(', ') : t('{n} cluster', { n: cluster.length })}
          </span>
        )}
      </button>
      {aperto && (
        <DataTable
          rows={hosts}
          columns={[
            { key: 'name', header: t('Host') },
            ...(conCluster ? [{ key: 'cluster', header: t('Cluster'), render: (r: HostVersione) => <span className="muted">{r.cluster}</span> }] : []),
          ]}
          rowKey={(r) => r.id}
          conteggio={{ nome: t('host') }}
        />
      )}
    </li>
  )
}

function RigaHost({
  host,
  maxVm,
  scelto,
  onScegli,
}: {
  host: VirtualHost
  maxVm: number
  scelto: boolean
  onScegli: () => void
}) {
  return (
    <li>
      <button
        type="button"
        className={`vmap__host venv-host${scelto ? ' vmap__item--on' : ''}`}
        onClick={onScegli}
      >
        <span className="venv-host__riga">
          <span className="vmap__name">{host.name}</span>
          {host.mgmt_ip && <span className="vmap__ip mono">{host.mgmt_ip}</span>}
          <span className="vmap__count">{t('{n} VM', { n: host.vm_count })}</span>
        </span>
        {/* La barretta rende confrontabili gli host a colpo d'occhio: chi
            porta il grosso delle VM si vede senza leggere i numeri. */}
        <span className="venv-host__carico">
          <span className="venv-host__carico-fill"
                style={{ width: `${(host.vm_count / maxVm) * 100}%` }} />
        </span>
      </button>
    </li>
  )
}

// ---------------------------------- scheda dell'host: tabelle o mappa (lazy)

function SchedaHost({ hostId }: { hostId: string | null }) {
  const [vista, setVista] = useState<'tabella' | 'mappa'>('tabella')
  const dettaglio = useAsync<VirtualHostDetail | null>(
    () => (hostId ? api.virtualMapHost(hostId) : Promise.resolve(null)),
    [hostId],
  )

  if (!hostId) {
    return (
      <Card title={t('Host')}>
        <p className="muted">{t('Scegli un host per vederne VM, datastore e reti.')}</p>
      </Card>
    )
  }
  if (dettaglio.loading && !dettaglio.data)
    return (
      <Card title={t('Host')}>
        <Spinner />
      </Card>
    )
  const d = dettaglio.data
  if (!d)
    return (
      <Card title={t('Host')}>
        <ErrorBanner error={dettaglio.error} />
      </Card>
    )

  return (
    <Card
      title={d.host.name}
      actions={
        <div className="vmap-vista">
          <button
            type="button"
            className={`vmap-vista__btn${vista === 'tabella' ? ' vmap-vista__btn--on' : ''}`}
            onClick={() => setVista('tabella')}
          >
            {t('Tabella')}
          </button>
          <button
            type="button"
            className={`vmap-vista__btn${vista === 'mappa' ? ' vmap-vista__btn--on' : ''}`}
            onClick={() => setVista('mappa')}
          >
            {t('Mappa')}
          </button>
        </div>
      }
    >
      <TestataHost d={d} />
      {vista === 'tabella' ? <TabelleHost d={d} /> : <MappaCorrelazioni d={d} />}
    </Card>
  )
}

/**
 * L'intestazione della scheda: i fatti dell'host e i due **meter** — vCPU
 * allocate contro core, RAM allocata contro fisica — calcolati dalle VM che
 * la scheda sta già mostrando. È il «quanto è carico» accanto al «cosa c'è».
 */
function TestataHost({ d }: { d: VirtualHostDetail }) {
  const info = d.host.info
  const cores = typeof info?.cpu_cores === 'number' ? info.cpu_cores : null
  const ram = typeof info?.memory_bytes === 'number' ? info.memory_bytes : null

  const vcpu = d.vms.reduce(
    (acc, v) => acc + (typeof v.info?.cpu_count === 'number' ? v.info.cpu_count : 0), 0)
  const vram = d.vms.reduce(
    (acc, v) => acc + (typeof v.info?.memory_bytes === 'number' ? v.info.memory_bytes : 0), 0)
  const accese = d.vms.filter((v) => String(v.info?.power_state ?? '') === 'on').length

  const chips: string[] = []
  if (info?.software_rev) chips.push(String(info.software_rev))
  if (info?.model) chips.push(String(info.model))
  if (d.host.mgmt_ip) chips.push(d.host.mgmt_ip)
  if (info?.state) chips.push(String(info.state))

  const ocCpu = cores ? vcpu / cores : null
  const pRam = ram ? (vram / ram) * 100 : null

  return (
    <div className="venv-testata">
      {chips.length > 0 && (
        <p className="vmap-scheda__info">{chips.join('  ·  ')}</p>
      )}
      <div className="venv-testata__metri">
        <Metro label={t('VM accese')} testo={`${accese}/${d.vms.length}`}
               percent={d.vms.length ? (accese / d.vms.length) * 100 : null} tono="ok" />
        <Metro label={t('vCPU / core')}
               testo={cores ? `${vcpu} / ${cores}${ocCpu != null ? ` (${ocCpu.toFixed(1)}×)` : ''}` : `${vcpu} / —`}
               percent={ocCpu != null ? Math.min(100, ocCpu * 25) : null}
               tono={tonoOvercommit(ocCpu)} />
        <Metro label={t('RAM allocata')}
               testo={ram ? `${fmtBytes(vram)} / ${fmtBytes(ram)}` : fmtBytes(vram)}
               percent={pRam} tono={tonoPercento(pRam)} />
      </div>
    </div>
  )
}

function Metro({
  label, testo, percent, tono,
}: { label: string; testo: string; percent: number | null; tono: 'ok' | 'warn' | 'err' | '' }) {
  return (
    <div className="venv-metro venv-metro--largo">
      <span className="venv-metro__testa">
        <span className="venv-metro__label">{label}</span>
        <span className={`venv-metro__valore venv-metro__valore--${tono}`}>{testo}</span>
      </span>
      {percent != null ? <MiniBarra percent={percent} tono={tono} /> : <span className="venv-mini" />}
    </div>
  )
}

function TabelleHost({ d }: { d: VirtualHostDetail }) {
  const navigate = useNavigate()
  // Le VM accese prima: sono quelle su cui si sta lavorando; le spente in
  // coda, dove non seppelliscono le altre.
  const vms = [...d.vms].sort((a, b) => {
    const pa = String(a.info?.power_state ?? '') === 'on' ? 0 : 1
    const pb = String(b.info?.power_state ?? '') === 'on' ? 0 : 1
    return pa - pb || a.name.localeCompare(b.name)
  })
  const byte = (n: VirtualNode, campo: string) => (typeof n.info?.[campo] === 'number' ? (n.info[campo] as number) : null)
  // No initial sort: the rows above are already in their order (powered on first).
  const colonneVm: Column<VirtualNode>[] = [
    { key: 'name', header: t('Nome') },
    {
      key: 'stato',
      header: t('Stato'),
      value: (v) => {
        const st = String(v.info?.power_state ?? '')
        return st ? (st.toLowerCase() === 'on' ? t('accesa') : t('spenta')) : ''
      },
      render: (v) => <StatoVm stato={v.info?.power_state} />,
    },
    {
      key: 'vcpu',
      header: 'vCPU',
      type: 'number',
      value: (v) => (typeof v.info?.cpu_count === 'number' ? v.info.cpu_count : null),
      render: (v) => numero(v.info?.cpu_count),
    },
    {
      key: 'ram',
      header: t('RAM'),
      type: 'number',
      value: (v) => byte(v, 'memory_bytes'),
      render: (v) => {
        const b = byte(v, 'memory_bytes')
        return b !== null ? fmtBytes(b) : '—'
      },
    },
    { key: 'guest_os', header: t('Guest OS'), value: (v) => (v.info?.guest_os ? String(v.info.guest_os) : '') },
  ]
  const colonneDatastore: Column<VirtualNode>[] = [
    { key: 'name', header: t('Nome') },
    {
      key: 'capacity',
      header: t('Capacità'),
      type: 'number',
      value: (ds) => byte(ds, 'capacity_bytes'),
      render: (ds) => {
        const b = byte(ds, 'capacity_bytes')
        return b !== null ? fmtBytes(b) : '—'
      },
    },
    {
      key: 'used_percent',
      header: t('Utilizzo'),
      align: 'left',
      type: 'number',
      value: (ds) => byte(ds, 'used_percent'),
      render: (ds) =>
        typeof ds.info?.used_percent === 'number' ? <BarraUso percent={ds.info.used_percent} /> : <span className="muted">{t('n/d')}</span>,
    },
  ]
  return (
    <>
      <SezioneTabella titolo={t('VM')} n={d.vms.length}>
        {d.vms.length === 0 ? (
          <p className="muted">{t('Nessuna VM su questo host.')}</p>
        ) : (
          <DataTable
            rows={vms}
            columns={colonneVm}
            rowKey={(v) => v.id}
            conteggio={{ nome: t('VM') }}
            onRowClick={(v) => navigate(`/cis/${v.id}`)}
          />
        )}
      </SezioneTabella>

      <SezioneTabella titolo={t('Datastore')} n={d.datastores.length}>
        {d.datastores.length === 0 ? (
          <p className="muted">{t('Nessun datastore usato dalle VM di questo host.')}</p>
        ) : (
          <DataTable
            rows={d.datastores}
            columns={colonneDatastore}
            rowKey={(ds) => ds.id}
            conteggio={{ nome: t('datastore') }}
            onRowClick={(ds) => navigate(`/cis/${ds.id}`)}
          />
        )}
      </SezioneTabella>

      <SezioneTabella titolo={t('Reti')} n={d.networks.length}>
        {d.networks.length === 0 ? (
          <p className="muted">{t('Nessuna rete usata dalle VM di questo host.')}</p>
        ) : (
          <div className="venv-reti">
            {d.networks.map((n) => (
              <button key={n.id} type="button" className="venv-rete"
                      onClick={() => navigate(`/cis/${n.id}`)}>
                <span className="venv-rete__nome">{n.name}</span>
                {n.info?.vlan != null && n.info.vlan !== '' && (
                  <span className="venv-rete__vlan">VLAN {String(n.info.vlan)}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </SezioneTabella>
    </>
  )
}

function SezioneTabella({ titolo, n, children }: { titolo: string; n: number; children: ReactNode }) {
  return (
    <div className="vmap__sezione">
      <h4>
        {titolo} <span className="vmap__count">({n})</span>
      </h4>
      {children}
    </div>
  )
}

function StatoVm({ stato }: { stato?: string | number }) {
  const s = String(stato ?? '')
  if (!s) return <span className="muted">—</span>
  const acceso = s.toLowerCase() === 'on'
  return (
    <span className={`vmap-stato${acceso ? ' vmap-stato--on' : ''}`}>
      {acceso ? t('accesa') : t('spenta')}
    </span>
  )
}

// ---------------------------------- vista mappa: settori per tipo

const MW = 720
const MH = 560

/**
 * La mappa delle correlazioni, **a settori**: l'host al centro, le VM
 * nell'arco superiore, i datastore in basso a sinistra, le reti in basso a
 * destra. La stella indistinta metteva una VM accanto a un datastore e
 * toccava leggerli dal colore; i settori danno a ogni tipo il suo posto, e la
 * mappa si legge come una frase — sopra il carico, sotto le dipendenze.
 *
 * Le etichette compaiono finché non si pestano (fino a `MAX_ETICHETTE` nodi
 * per settore); oltre, i nodi restano — col nome nel tooltip — e il conteggio
 * del settore dice quanti sono. I datastore portano il riempimento nel
 * colore; le VM spente sono spente anche qui.
 */
const MAX_ETICHETTE = 18

function MappaCorrelazioni({ d }: { d: VirtualHostDetail }) {
  const navigate = useNavigate()
  const cx = MW / 2
  const cy = MH / 2 + 10

  type Nodo = {
    id: string; nome: string; tipo: 'vm' | 'datastore' | 'network'
    x: number; y: number; info?: VirtualNode['info']
  }

  const nodi = useMemo<Nodo[]>(() => {
    const out: Nodo[] = []
    // Le VM sull'arco superiore (da 200° a -20°, senso orario), su uno o due
    // anelli quando sono tante: l'arco resta leggibile fino a ~40 macchine.
    const vms = [...d.vms].sort((a, b) => {
      const pa = String(a.info?.power_state ?? '') === 'on' ? 0 : 1
      const pb = String(b.info?.power_state ?? '') === 'on' ? 0 : 1
      return pa - pb || a.name.localeCompare(b.name)
    })
    const anelli = vms.length > 22 ? 2 : 1
    vms.forEach((v, i) => {
      const anello = i % anelli
      const dentro = Math.floor(i / anelli)
      const nAnello = Math.ceil(vms.length / anelli)
      const f = nAnello <= 1 ? 0.5 : dentro / (nAnello - 1)
      const angolo = Math.PI * (1.1 - 1.2 * f) // 198° → -18°
      const r = 175 + anello * 55
      out.push({
        id: v.id, nome: v.name, tipo: 'vm', info: v.info,
        x: cx + r * Math.cos(angolo), y: cy - r * Math.sin(angolo) - 20,
      })
    })
    // Datastore in basso a sinistra, reti in basso a destra: due file.
    d.datastores.forEach((s, i) => {
      const perRiga = 3
      out.push({
        id: s.id, nome: s.name, tipo: 'datastore', info: s.info,
        x: cx - 260 + (i % perRiga) * 90,
        y: cy + 135 + Math.floor(i / perRiga) * 58,
      })
    })
    d.networks.forEach((n, i) => {
      const perRiga = 3
      out.push({
        id: n.id, nome: n.name, tipo: 'network', info: n.info,
        x: cx + 90 + (i % perRiga) * 90,
        y: cy + 135 + Math.floor(i / perRiga) * 58,
      })
    })
    return out
  }, [d, cx, cy])

  const etichette = {
    vm: d.vms.length <= MAX_ETICHETTE,
    datastore: d.datastores.length <= MAX_ETICHETTE,
    network: d.networks.length <= MAX_ETICHETTE,
  }
  // La posizione di ogni nodo nel suo settore, per lo sfalsamento delle
  // etichette (le VM vicine all'apice dell'arco si pestano i nomi).
  const indice = new Map(nodi.map((n, i) => [n.id, i]))

  return (
    <div className="vmap-mappa">
      <svg viewBox={`0 0 ${MW} ${MH}`} className="vmap-mappa__svg" role="img"
           aria-label={t('Mappa delle correlazioni dell’host')}>
        {/* i settori, nominati: la geografia della mappa */}
        <text x={cx} y={26} className="venv-mappa__settore" textAnchor="middle">
          {t('VM')} ({d.vms.length})
        </text>
        <text x={cx - 215} y={cy + 105} className="venv-mappa__settore" textAnchor="middle">
          {t('Datastore')} ({d.datastores.length})
        </text>
        <text x={cx + 180} y={cy + 105} className="venv-mappa__settore" textAnchor="middle">
          {t('Reti')} ({d.networks.length})
        </text>

        {/* gli archi, prima dei nodi */}
        {nodi.map((n) => (
          <path key={`e-${n.id}`}
                d={`M${cx},${cy} Q${(cx + n.x) / 2},${(cy + n.y) / 2 + (n.tipo === 'vm' ? -14 : 14)} ${n.x},${n.y}`}
                className={`venv-mappa__arco venv-mappa__arco--${n.tipo}`} />
        ))}

        {/* i nodi */}
        {nodi.map((n) => {
          const spenta = n.tipo === 'vm' && String(n.info?.power_state ?? '') !== '' &&
            String(n.info?.power_state) !== 'on'
          return (
            <g key={n.id} className="vmap-mappa__nodo" transform={`translate(${n.x} ${n.y})`}
               onClick={() => navigate(`/cis/${n.id}`)}>
              <title>{n.nome}{infoTooltip(n.tipo, n.info)}</title>
              {n.tipo === 'vm' && (
                <circle r={7} className={`venv-nodo venv-nodo--vm${spenta ? ' venv-nodo--spenta' : ''}`} />
              )}
              {n.tipo === 'datastore' && (
                <NodoDatastore percent={typeof n.info?.used_percent === 'number' ? n.info.used_percent : null} />
              )}
              {n.tipo === 'network' && (
                <rect x={-7} y={-7} width={14} height={14} transform="rotate(45)"
                      className="venv-nodo venv-nodo--rete" />
              )}
              {etichette[n.tipo] && (
                // Le etichette delle VM si sfalsano su due quote alternate:
                // all'apice dell'arco i nodi si avvicinano e due nomi alla
                // stessa altezza si pestano.
                <text x={0} y={n.tipo === 'vm' ? (indice.get(n.id)! % 2 === 0 ? -12 : -26) : 22}
                      className="vmap-mappa__etichetta" textAnchor="middle">
                  {accorcia(n.nome)}
                </text>
              )}
            </g>
          )
        })}

        {/* l'host, al centro e sopra tutto */}
        <g className="vmap-mappa__nodo" transform={`translate(${cx} ${cy})`}>
          <circle r={26} className="venv-nodo venv-nodo--host-alone" />
          <circle r={16} className="venv-nodo venv-nodo--host" />
          <text x={0} y={44} className="venv-mappa__host" textAnchor="middle">{d.host.name}</text>
        </g>
      </svg>
      <div className="vmap-mappa__legenda">
        <span className="vmap-mappa__voce"><span className="venv-legenda venv-legenda--host" />{t('Host')}</span>
        <span className="vmap-mappa__voce"><span className="venv-legenda venv-legenda--vm" />{t('VM')} ({t('piena = accesa')})</span>
        <span className="vmap-mappa__voce"><span className="venv-legenda venv-legenda--ds" />{t('Datastore')} ({t('colore = riempimento')})</span>
        <span className="vmap-mappa__voce"><span className="venv-legenda venv-legenda--rete" />{t('Rete')}</span>
      </div>
    </div>
  )
}

/** Il datastore è un cerchio il cui colore dice quanto è pieno. */
function NodoDatastore({ percent }: { percent: number | null }) {
  const tono = percent == null ? '' : percent >= 95 ? 'err' : percent >= 85 ? 'warn' : 'ok'
  return (
    <>
      <rect x={-9} y={-9} width={18} height={18} rx={4}
            className={`venv-nodo venv-nodo--ds venv-nodo--ds-${tono || 'nd'}`} />
      {percent != null && (
        <text x={0} y={3.5} className="venv-nodo__percento" textAnchor="middle">
          {Math.round(percent)}
        </text>
      )}
    </>
  )
}

function infoTooltip(tipo: string, info?: VirtualNode['info']): string {
  if (!info) return ''
  const parti: string[] = []
  if (tipo === 'vm') {
    if (info.power_state) parti.push(String(info.power_state) === 'on' ? t('accesa') : t('spenta'))
    if (info.cpu_count) parti.push(`${info.cpu_count} vCPU`)
    if (typeof info.memory_bytes === 'number') parti.push(fmtBytes(info.memory_bytes))
  }
  if (tipo === 'datastore' && typeof info.used_percent === 'number') {
    parti.push(`${Math.round(info.used_percent)}%`)
  }
  if (tipo === 'network' && info.vlan != null && info.vlan !== '') {
    parti.push(`VLAN ${info.vlan}`)
  }
  return parti.length ? ` — ${parti.join(', ')}` : ''
}

function accorcia(nome: string): string {
  return nome.length > 16 ? `${nome.slice(0, 15)}…` : nome
}

// ------------------------------------------------- pezzetti condivisi

function BarraUso({ percent, etichetta }: { percent: number; etichetta?: string }) {
  const p = Math.max(0, Math.min(100, percent))
  return (
    <div className="cap-barra" title={`${Math.round(percent)}%`}>
      <div className={`cap-barra__fill${p >= 85 ? ' cap-barra__fill--warn' : ''}`} style={{ width: `${p}%` }} />
      <span className="cap-barra__num">{etichetta ?? `${Math.round(percent)}%`}</span>
    </div>
  )
}

function numero(v?: string | number): string {
  return typeof v === 'number' ? String(v) : v ? String(v) : '—'
}

function fmtBytes(bytes: number): string {
  if (!bytes) return '0'
  const tb = bytes / 1024 ** 4
  if (tb >= 1) return `${tb.toFixed(tb >= 10 ? 0 : 1)} TB`
  const g = bytes / 1024 ** 3
  if (g >= 1) return `${g.toFixed(g >= 10 ? 0 : 1)} GB`
  return `${Math.round(bytes / 1024 ** 2)} MB`
}
